/**
 * GapsResults.js
 * Renders all result cards after a gap analysis completes.
 *
 * Layout fixes vs previous version:
 *  - Score comparison: 3-column grid (yours | delta | theirs) — no blank right side
 *  - Engagement signals: compact inline rows, column headers above the rows
 *  - All cards are self-contained, easy to add/remove independently
 */
import React, { useState } from 'react';
import {
    Box, Grid, Paper, Typography, Chip, Avatar,
    Divider, LinearProgress, Alert, alpha, Tooltip,
    IconButton, Snackbar,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    TrendingUp as TrendingUpIcon,
    Tag as TagIcon,
    Search as SearchIcon,
    AutoAwesome as AutoAwesomeIcon,
    EditNote as EditNoteIcon,
    ContentCopy as CopyIcon,
    CheckCircleOutline as CopiedIcon,
} from '@mui/icons-material';
import { ScoreRing, SignalRow } from './GapsPrimitives';
import { PRIORITY_COLOR, PRIORITY_BG, SIGNAL_DEFINITIONS } from './constants';

// ── 1. Score comparison card ─────────────────────────────────────────────────
function ScoreCard({ your, competitor, gaps, card }) {
    const delta = competitor.overall_score - your.overall_score;
    const isAhead = delta <= 0;

    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AnalyticsIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Overall Score Comparison</Typography>
            </Box>

            {/* 3-column: yours | delta badge | theirs */}
            <Grid container alignItems="center" spacing={2}>
                {/* Your score */}
                <Grid xs={4} sx={{ textAlign: 'center' }}>
                    <ScoreRing
                        value={your.overall_score}
                        label="Your Content"
                        sub={`${your.word_count} words · ${your.readability}/10 read`}
                        color="#6366f1"
                        size={104}
                    />
                </Grid>

                {/* Delta badge */}
                <Grid xs={4} sx={{ textAlign: 'center' }}>
                    <Box sx={{
                        display: 'inline-flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        py: 1.5, px: 2.5, borderRadius: 3,
                        background: isAhead
                            ? `linear-gradient(135deg, ${alpha('#10b981', 0.08)}, ${alpha('#6366f1', 0.08)})`
                            : `linear-gradient(135deg, ${alpha('#ef4444', 0.08)}, ${alpha('#f59e0b', 0.08)})`,
                        border: `1px solid ${alpha(isAhead ? '#10b981' : '#ef4444', 0.2)}`,
                    }}>
                        <Typography sx={{
                            fontWeight: 900, fontSize: '2rem', lineHeight: 1,
                            color: isAhead ? '#10b981' : '#ef4444',
                        }}>
                            {isAhead ? `+${Math.abs(delta)}` : `-${Math.abs(delta)}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.25 }}>
                            {isAhead ? 'pts ahead' : 'pts behind'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Competitor score */}
                <Grid xs={4} sx={{ textAlign: 'center' }}>
                    <ScoreRing
                        value={competitor.overall_score}
                        label="Top Performer"
                        sub={`${competitor.word_count} words · ${competitor.readability}/10 read`}
                        color="#10b981"
                        size={104}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* Topic overlap bar */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Topic Overlap
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#6366f1' }}>
                        {Math.round(gaps.topic_overlap * 100)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.round(gaps.topic_overlap * 100)}
                    sx={{
                        height: 7, borderRadius: 4,
                        bgcolor: alpha('#6366f1', 0.1),
                        '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                            borderRadius: 4,
                        },
                    }}
                />
                <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                    {gaps.topic_overlap < 0.3
                        ? 'Low overlap — significant topic gaps exist'
                        : gaps.topic_overlap < 0.6
                            ? 'Moderate overlap — some topics to cover'
                            : 'High overlap — content is well-aligned'}
                </Typography>
            </Box>
        </Paper>
    );
}

// ── 2. Engagement signals card ───────────────────────────────────────────────
function SignalsCard({ your, competitor, gaps, card }) {
    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#10b981', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Engagement Signals</Typography>
                {gaps.missing_signals.length > 0 && (
                    <Chip
                        label={`${gaps.missing_signals.length} missing`}
                        size="small"
                        sx={{ ml: 'auto', bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                    />
                )}
            </Box>

            {/* Column headers */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                gap: 1, mb: 0.5,
            }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#6366f1', textAlign: 'right', fontSize: '0.72rem', letterSpacing: 0.5 }}>
                    YOURS
                </Typography>
                <Box sx={{ minWidth: 120 }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#10b981', textAlign: 'left', fontSize: '0.72rem', letterSpacing: 0.5 }}>
                    TOP PERFORMER
                </Typography>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Signal rows */}
            {SIGNAL_DEFINITIONS.map(({ key, label }) => (
                <SignalRow
                    key={key}
                    label={label}
                    yours={your.signals[key]}
                    theirs={competitor.signals[key]}
                />
            ))}

            {gaps.missing_signals.length > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mt: 1.5, borderRadius: 2, py: 0.5, fontSize: '0.8rem' }}
                >
                    Add {gaps.missing_signals.map(s => s.signal).join(', ')} to match top content
                </Alert>
            )}
        </Paper>
    );
}

// ── 3. Missing keywords card ─────────────────────────────────────────────────
function KeywordsCard({ gaps, card }) {
    if (!gaps.missing_keywords.length) return null;
    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <TagIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Missing Keywords</Typography>
                <Chip
                    label={gaps.missing_keywords.length}
                    size="small"
                    sx={{ ml: 'auto', bgcolor: alpha('#8b5cf6', 0.1), color: '#7c3aed', fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                />
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.82rem' }}>
                In top content but missing from yours — weave them in naturally.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {gaps.missing_keywords.map((kw, i) => (
                    <Chip
                        key={i}
                        label={`${kw.word}  ×${kw.frequency}`}
                        size="small"
                        sx={{
                            bgcolor: alpha('#8b5cf6', 0.07),
                            color: '#7c3aed',
                            border: `1px solid ${alpha('#8b5cf6', 0.22)}`,
                            fontWeight: 600, fontSize: '0.76rem',
                            opacity: Math.max(0.45, 1 - (i / gaps.missing_keywords.length) * 0.55),
                        }}
                    />
                ))}
            </Box>
        </Paper>
    );
}

// ── 4. Topic gaps card ───────────────────────────────────────────────────────
function TopicsCard({ gaps, card }) {
    if (!gaps.missing_topics.length) return null;
    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <SearchIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Topic Gaps</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {gaps.missing_topics.map((t, i) => (
                    <Chip
                        key={i} label={t} size="small"
                        sx={{
                            bgcolor: alpha('#f59e0b', 0.1), color: '#d97706',
                            border: `1px solid ${alpha('#f59e0b', 0.25)}`, fontWeight: 600,
                        }}
                    />
                ))}
            </Box>
        </Paper>
    );
}

// ── 5. Recommendations card ──────────────────────────────────────────────────
function RecommendationsCard({ recommendations, card }) {
    if (!recommendations.length) return null;
    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <AutoAwesomeIcon sx={{ color: '#ec4899', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Actionable Recommendations</Typography>
                <Chip
                    label={`${recommendations.length} actions`}
                    size="small"
                    sx={{ ml: 'auto', bgcolor: alpha('#ec4899', 0.1), color: '#ec4899', fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {recommendations.map((rec, i) => {
                    const color = PRIORITY_COLOR[rec.priority] || '#6366f1';
                    const bg = PRIORITY_BG[rec.priority] || alpha('#6366f1', 0.08);
                    return (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex', gap: 1.75, p: 2, borderRadius: 2.5,
                                background: bg, border: `1px solid ${alpha(color, 0.2)}`,
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 16px ${alpha(color, 0.14)}` },
                            }}
                        >
                            <Avatar sx={{
                                bgcolor: alpha(color, 0.15), color,
                                width: 32, height: 32, flexShrink: 0,
                                fontSize: '0.8rem', fontWeight: 800,
                            }}>
                                {i + 1}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {rec.title}
                                    </Typography>
                                    <Chip label={rec.category} size="small"
                                        sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 600, height: 18, fontSize: '0.66rem' }} />
                                    <Chip label={rec.priority.toUpperCase()} size="small"
                                        sx={{ bgcolor: alpha(color, 0.18), color, fontWeight: 700, height: 18, fontSize: '0.66rem' }} />
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55, fontSize: '0.81rem' }}>
                                    {rec.description}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}

// ── 6. Suggested post rewrites card ─────────────────────────────────────────
const VARIANT_COLORS = ['#6366f1', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'];

function SuggestionItem({ suggestion, index, onCopy, copiedIdx }) {
    const color = VARIANT_COLORS[index % VARIANT_COLORS.length];
    const isCopied = copiedIdx === index;

    return (
        <Box sx={{
            p: 2.5, borderRadius: 2.5,
            border: `1px solid ${alpha(color, 0.22)}`,
            background: `linear-gradient(135deg, ${alpha(color, 0.04)}, ${alpha(color, 0.02)})`,
            transition: 'box-shadow 0.2s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
                transform: 'translateY(-1px)',
            },
        }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Avatar sx={{
                    bgcolor: alpha(color, 0.14), color,
                    width: 28, height: 28, fontSize: '0.75rem', fontWeight: 800, flexShrink: 0,
                }}>
                    {index + 1}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
                    {suggestion.label}
                </Typography>
                <Chip
                    label={suggestion.improvement}
                    size="small"
                    sx={{
                        bgcolor: alpha(color, 0.12), color,
                        border: `1px solid ${alpha(color, 0.25)}`,
                        fontWeight: 700, height: 20, fontSize: '0.67rem',
                    }}
                />
                <Tooltip title={isCopied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                        size="small"
                        onClick={() => onCopy(suggestion.text, index)}
                        sx={{
                            color: isCopied ? '#10b981' : 'text.disabled',
                            '&:hover': { color, bgcolor: alpha(color, 0.08) },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {isCopied
                            ? <CopiedIcon sx={{ fontSize: 17 }} />
                            : <CopyIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Description */}
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 1.25, fontSize: '0.75rem' }}>
                {suggestion.description}
            </Typography>

            {/* Post text */}
            <Box sx={{
                p: 1.75, borderRadius: 2,
                bgcolor: alpha(color, 0.04),
                border: `1px solid ${alpha(color, 0.12)}`,
                fontFamily: 'inherit',
            }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.primary', lineHeight: 1.7,
                        fontSize: '0.88rem', whiteSpace: 'pre-wrap',
                    }}
                >
                    {suggestion.text}
                </Typography>
            </Box>
        </Box>
    );
}

function SuggestionsCard({ suggestedPosts, card }) {
    const [copiedIdx, setCopiedIdx] = useState(null);
    const [snackOpen, setSnackOpen] = useState(false);

    if (!suggestedPosts || !suggestedPosts.length) return null;

    const handleCopy = (text, idx) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIdx(idx);
            setSnackOpen(true);
            setTimeout(() => setCopiedIdx(null), 2000);
        });
    };

    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EditNoteIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Suggested Post Rewrites</Typography>
                <Chip
                    label={`${suggestedPosts.length} variants`}
                    size="small"
                    sx={{ ml: 'auto', bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                />
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, fontSize: '0.83rem' }}>
                Ready-to-use rewrites of your post — each one fixes a specific gap. Click the copy icon to grab any version.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                {suggestedPosts.map((s, i) => (
                    <SuggestionItem
                        key={i}
                        suggestion={s}
                        index={i}
                        onCopy={handleCopy}
                        copiedIdx={copiedIdx}
                    />
                ))}
            </Box>

            <Snackbar
                open={snackOpen}
                autoHideDuration={2000}
                onClose={() => setSnackOpen(false)}
                message="Copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Paper>
    );
}


export default function GapsResults({ result, card }) {
    const { your, competitor, gaps, recommendations, suggested_posts } = result;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <ScoreCard your={your} competitor={competitor} gaps={gaps} card={card} />
            <SignalsCard your={your} competitor={competitor} gaps={gaps} card={card} />
            <SuggestionsCard suggestedPosts={suggested_posts} card={card} />
            <KeywordsCard gaps={gaps} card={card} />
            <TopicsCard gaps={gaps} card={card} />
            <RecommendationsCard recommendations={recommendations} card={card} />
        </Box>
    );
}

